import { Injectable } from '@angular/core';

import { ContentDSLComponentOutline } from '../content.model';

enum CaptureType {
    KEY,
    VALUE
}

enum SpecialCharacters {
    CAPTURE_GROUP_OPEN = '{',
    CAPTURE_GROUP_CLOSE = '}',
    KEY_VALUE_SEPARATOR = '=',
    KEY_VALUE_PAIR_SEPARATOR = ':'
}

@Injectable()
export class ApContentDSLService {
    public parseDSLStrings(content: string): string[] {
        const dslStrings = [];
        const stringArray = content.split('');
        const openingDelimiters = [];
        for (let i = 0; i < stringArray.length; i++) {
            const currentCharacter = stringArray[i];
            const nextCharacter = stringArray[i + 1];

            if (currentCharacter === '[' && nextCharacter === '[') {
                // Skip the next character to support nested items with successive delimiters e.g. [[[[
                i++;
                openingDelimiters.push(i + 1);
            }

            if (currentCharacter === ']' && nextCharacter === ']') {
                // Pop the latest opening delimiter from the stack
                // If the stack is now empty we found the delimiter pair we were looking for
                const openingsPos = openingDelimiters.pop();
                if (openingsPos != null && openingDelimiters.length === 0) {
                    dslStrings.push(content.substring(openingsPos, i));
                }
                // Skip the next character to support nested items with successive delimiters e.g. ]]]]
                i++;
            }
        }
        return dslStrings;
    }

    public createComponentOutline(tag: string): ContentDSLComponentOutline {
        const tagWithoutContent = tag.replace(/:@.*$/, '');
        const componentName = tag.split(':')[0];
        const params = tagWithoutContent.replace(`${componentName}:`, '');

        return {
            componentName,
            componentInputs: this.getComponentProperties(params),
            componentProjectedHtml: this.getComponentProjectedHtml(tag)
        };
    }

    private getComponentProperties(params: string): { [key: string]: any } {
        const paramMap = this.getParamMap(params);
        for (const key in paramMap) {
            if (paramMap.hasOwnProperty(key)) {
                paramMap[key] = this.safeParseValue(paramMap[key]);
            }
        }

        return paramMap;
    }

    private getComponentProjectedHtml(tag: string): string {
        const projectedHtmlMatch = /:@(.*?)$/.exec(tag);
        return projectedHtmlMatch ? projectedHtmlMatch[1] : '';
    }

    private safeParseValue(value: string): any {
        try {
            return JSON.parse(value);
        } catch (e) {
            return value;
        }
    }

    private getParamMap(paramsString: string): { [key: string]: string } {
        const paramMap = {};

        let key = '';
        let captureGroup = false;
        let type = CaptureType.KEY;

        const addCharToKey = (paramKey: string, char: string): void => {
            if (!paramMap[paramKey]) {
                paramMap[paramKey] = '';
            }
            paramMap[paramKey] += char;
        };

        for (let index = 0; index < paramsString.length; index++) {
            const char = paramsString[index];

            // Check for closing capture group char
            if (
                char === SpecialCharacters.CAPTURE_GROUP_CLOSE &&
                (paramsString[index + 1] === SpecialCharacters.KEY_VALUE_PAIR_SEPARATOR || paramsString[index + 1] === undefined)
            ) {
                captureGroup = false;
                type = CaptureType.KEY;
                continue;
            }

            // If capture group is true, add any character to the value
            if (captureGroup) {
                addCharToKey(key, char);
                continue;
            }

            // Check for opening capture group char
            if (char === SpecialCharacters.CAPTURE_GROUP_OPEN && paramsString[index - 1] === SpecialCharacters.KEY_VALUE_SEPARATOR) {
                captureGroup = true;
            } else if (char === SpecialCharacters.KEY_VALUE_SEPARATOR) {
                type = CaptureType.VALUE;
            } else if (char === SpecialCharacters.KEY_VALUE_PAIR_SEPARATOR) {
                type = CaptureType.KEY;
                key = '';
            } else {
                // Add to key or value
                if (type === CaptureType.KEY) {
                    key += char;
                } else {
                    addCharToKey(key, char);
                }
            }
        }

        return paramMap;
    }
}
